import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Package,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Loader2,
  Search,
  Apple,
  Sparkles,
  Barcode,
  ChevronRight,
  Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { ImageUpload } from '@/components/ui/ImageUpload';

const supabase = getSupabaseAdminClient();

type ProductType = 'food' | 'cosmetic';

// Nutrition Facts Types
interface NutritionRow {
  id: string;
  name: string;
  value: string;
  indent: boolean;
}

interface NutritionFacts {
  reference_amount: number;
  reference_unit: 'g' | 'ml';
  rows: NutritionRow[];
}

interface ManualProduct {
  id: string;
  barcode: string;
  name: string;
  brand: string;
  categories: string | null;
  ingredients_text: string | null;
  ingredients_tags: string[] | null;
  image_url: string | null;
  nutriscore_grade: string | null;
  nutrition_facts: NutritionFacts | null;
  product_type: ProductType;
  is_manual: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface ProductFormData {
  barcode: string;
  name: string;
  brand: string;
  categories: string;
  ingredients_text: string;
  image_url: string;
  nutriscore_grade: string;
  nutrition_facts: NutritionFacts | null;
  product_type: ProductType;
}

// Aus einem Nutzer-Vorschlag übernehmbare Felder (product_suggestions deckt
// keine sicherheitsrelevanten Felder wie ingredients_text ab)
interface SuggestionPrefill {
  barcode: string;
  name: string;
  brand: string;
  product_type: ProductType;
  notes: string | null;
}

// Helper to generate unique IDs
function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// Nutrition Table Component
function NutritionTable({
  nutritionFacts,
  onChange,
  productType,
}: {
  nutritionFacts: NutritionFacts | null;
  onChange: (facts: NutritionFacts | null) => void;
  productType: ProductType;
}) {
  // Initialize with default values if null
  const facts: NutritionFacts = nutritionFacts || {
    reference_amount: 100,
    reference_unit: productType === 'food' ? 'g' : 'ml',
    rows: [],
  };

  const handleReferenceAmountChange = (value: string) => {
    const amount = parseInt(value, 10) || 100;
    onChange({ ...facts, reference_amount: amount });
  };

  const handleReferenceUnitChange = (unit: 'g' | 'ml') => {
    onChange({ ...facts, reference_unit: unit });
  };

  const handleRowChange = (id: string, field: 'name' | 'value', value: string) => {
    const newRows = facts.rows.map((row) =>
      row.id === id ? { ...row, [field]: value } : row
    );
    onChange({ ...facts, rows: newRows });
  };

  const handleAddRow = (afterId: string | null, indent: boolean) => {
    const newRow: NutritionRow = {
      id: generateId(),
      name: '',
      value: '',
      indent,
    };

    if (afterId === null) {
      // Add at the end
      onChange({ ...facts, rows: [...facts.rows, newRow] });
    } else {
      // Find position and insert after
      const index = facts.rows.findIndex((r) => r.id === afterId);
      if (index !== -1) {
        const newRows = [...facts.rows];
        newRows.splice(index + 1, 0, newRow);
        onChange({ ...facts, rows: newRows });
      }
    }
  };

  const handleRemoveRow = (id: string) => {
    const newRows = facts.rows.filter((row) => row.id !== id);
    onChange(newRows.length > 0 ? { ...facts, rows: newRows } : null);
  };

  const handleToggleIndent = (id: string) => {
    const newRows = facts.rows.map((row) =>
      row.id === id ? { ...row, indent: !row.indent } : row
    );
    onChange({ ...facts, rows: newRows });
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-slate-700">Nährwertangaben</label>

      {/* Reference Amount */}
      <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
        <span className="text-sm text-slate-600">pro</span>
        <input
          type="number"
          value={facts.reference_amount}
          onChange={(e) => handleReferenceAmountChange(e.target.value)}
          min={1}
          className="w-20 px-2 py-1 border border-slate-200 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
        />
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => handleReferenceUnitChange('g')}
            className={`px-3 py-1 rounded text-sm font-medium transition-all ${
              facts.reference_unit === 'g'
                ? 'bg-teal-500 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            g
          </button>
          <button
            type="button"
            onClick={() => handleReferenceUnitChange('ml')}
            className={`px-3 py-1 rounded text-sm font-medium transition-all ${
              facts.reference_unit === 'ml'
                ? 'bg-teal-500 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            ml
          </button>
        </div>
      </div>

      {/* Nutrition Rows */}
      <div className="space-y-2">
        {facts.rows.map((row) => (
          <div key={row.id} className="flex items-center gap-2">
            {/* Indent indicator / toggle */}
            <button
              type="button"
              onClick={() => handleToggleIndent(row.id)}
              className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
                row.indent
                  ? 'bg-teal-100 text-teal-600'
                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
              }`}
              title={row.indent ? 'Einrückung entfernen' : 'Einrücken (Unterbestandteil)'}
            >
              <ChevronRight className={`w-4 h-4 ${row.indent ? '' : 'opacity-50'}`} />
            </button>

            {/* Name Input */}
            <input
              type="text"
              value={row.name}
              onChange={(e) => handleRowChange(row.id, 'name', e.target.value)}
              placeholder={row.indent ? 'z.B. davon Zucker' : 'z.B. Kohlenhydrate'}
              className={`flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 ${
                row.indent ? 'ml-4 bg-slate-50' : ''
              }`}
            />

            {/* Value Input */}
            <input
              type="text"
              value={row.value}
              onChange={(e) => handleRowChange(row.id, 'value', e.target.value)}
              placeholder="z.B. 20g"
              className="w-28 px-3 py-2 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />

            {/* Add Sub-Row Button (only for non-indented rows) */}
            {!row.indent && (
              <button
                type="button"
                onClick={() => handleAddRow(row.id, true)}
                className="p-1.5 text-teal-500 hover:bg-teal-50 rounded transition-colors"
                title="Unterbestandteil hinzufügen"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}

            {/* Delete Row Button */}
            <button
              type="button"
              onClick={() => handleRemoveRow(row.id)}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Zeile entfernen"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}

        {/* Add Row Button */}
        <button
          type="button"
          onClick={() => handleAddRow(null, false)}
          className="w-full py-2 border-2 border-dashed border-slate-200 rounded-lg text-sm text-slate-500 hover:border-teal-300 hover:text-teal-600 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nährwert hinzufügen
        </button>
      </div>

      {/* Hint */}
      <p className="text-xs text-slate-400">
        Klicke auf den Pfeil um Zeilen einzurücken (z.B. "davon Zucker" unter "Kohlenhydrate").
        Das + bei einer Zeile fügt direkt darunter einen eingerückten Unterbestandteil hinzu.
      </p>
    </div>
  );
}

// Product Form Component
function ProductForm({
  product,
  prefill,
  onSave,
  onCancel,
  isLoading,
}: {
  product?: ManualProduct;
  prefill?: SuggestionPrefill;
  onSave: (data: ProductFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<ProductFormData>({
    barcode: product?.barcode || prefill?.barcode || '',
    name: product?.name || prefill?.name || '',
    brand: product?.brand || prefill?.brand || '',
    categories: product?.categories || '',
    ingredients_text: product?.ingredients_text || '',
    image_url: product?.image_url || '',
    nutriscore_grade: product?.nutriscore_grade || '',
    nutrition_facts: product?.nutrition_facts || null,
    product_type: product?.product_type || prefill?.product_type || 'food',
  });

  const handleChange = (field: keyof ProductFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNutritionFactsChange = (facts: NutritionFacts | null) => {
    setFormData((prev) => ({ ...prev, nutrition_facts: facts }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-900">
          {product ? 'Produkt bearbeiten' : 'Neues Produkt anlegen'}
        </h3>
        <button type="button" onClick={onCancel} className="p-2 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      {prefill && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-800">Aus Nutzer-Vorschlag übernommen</p>
            <p className="text-sm text-amber-700">
              Marke, Name, Typ und Barcode wurden übernommen. Bitte Zutaten und weitere fehlende Angaben ergänzen.
            </p>
            {prefill.notes && (
              <p className="mt-1 text-xs text-amber-600">Notiz vom Nutzer: „{prefill.notes}“</p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Product Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Produkttyp *</label>
            <div className="flex gap-3">
              {[
                { value: 'food', label: 'Lebensmittel', icon: Apple, color: 'text-green-600 border-green-500 bg-green-50' },
                { value: 'cosmetic', label: 'Kosmetik', icon: Sparkles, color: 'text-pink-600 border-pink-500 bg-pink-50' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('product_type', option.value)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                    formData.product_type === option.value
                      ? option.color
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <option.icon className="w-4 h-4" />
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Barcode */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Barcode (EAN) *
            </label>
            <div className="relative">
              <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.barcode}
                onChange={(e) => handleChange('barcode', e.target.value)}
                required
                placeholder="z.B. 4014400400014"
                pattern="[0-9]{8,14}"
                title="8-14 stellige Nummer"
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
            <p className="text-xs text-slate-400 mt-1">8-14 stellige EAN-Nummer</p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Produktname *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              placeholder="z.B. Bio Hafermilch Original"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>

          {/* Brand */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Marke / Hersteller *</label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => handleChange('brand', e.target.value)}
              required
              placeholder="z.B. Oatly"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Kategorien</label>
            <input
              type="text"
              value={formData.categories}
              onChange={(e) => handleChange('categories', e.target.value)}
              placeholder="z.B. Getränke, Pflanzenmilch, Bio"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
            <p className="text-xs text-slate-400 mt-1">Kommagetrennte Kategorien</p>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Image Upload */}
          <ImageUpload
            value={formData.image_url}
            onChange={(url) => setFormData((prev) => ({ ...prev, image_url: url }))}
            bucket="ProductImages"
            label="Produktbild"
            placeholder="https://example.com/produkt.jpg"
          />

          {/* Nutri-Score (only for food) */}
          {formData.product_type === 'food' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nutri-Score</label>
              <div className="flex gap-2">
                {['a', 'b', 'c', 'd', 'e', ''].map((grade) => (
                  <button
                    key={grade}
                    type="button"
                    onClick={() => handleChange('nutriscore_grade', grade)}
                    className={`w-10 h-10 rounded-lg font-bold uppercase transition-all ${
                      formData.nutriscore_grade === grade
                        ? grade === 'a'
                          ? 'bg-green-500 text-white'
                          : grade === 'b'
                          ? 'bg-lime-500 text-white'
                          : grade === 'c'
                          ? 'bg-yellow-500 text-white'
                          : grade === 'd'
                          ? 'bg-orange-500 text-white'
                          : grade === 'e'
                          ? 'bg-red-500 text-white'
                          : 'bg-slate-200 text-slate-600'
                        : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                    }`}
                  >
                    {grade || '—'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Ingredients */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Inhaltsstoffe *</label>
            <textarea
              value={formData.ingredients_text}
              onChange={(e) => handleChange('ingredients_text', e.target.value)}
              required
              rows={6}
              placeholder="z.B. Wasser, Hafer 10%, Sonnenblumenöl, Calciumcarbonat..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
            <p className="text-xs text-slate-400 mt-1">
              Wie auf der Verpackung angegeben. Wichtig für die Sicherheitsbewertung!
            </p>
          </div>
        </div>
      </div>

      {/* Nutrition Facts (only for food) */}
      {formData.product_type === 'food' && (
        <div className="mt-6 pt-6 border-t">
          <NutritionTable
            nutritionFacts={formData.nutrition_facts}
            onChange={handleNutritionFactsChange}
            productType={formData.product_type}
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={isLoading || !formData.barcode || !formData.name || !formData.brand || !formData.ingredients_text}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {product ? 'Aktualisieren' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}

export function ManualProductsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromSuggestionId = searchParams.get('fromSuggestion');

  // Die Vorschlags-Bindung ist ein One-Shot: nach Abbrechen oder erfolgreicher
  // Anlage entfernen wir den Param, damit ein anschliessendes "Neues Produkt"
  // nicht erneut mit dem Vorschlag vorausgefuellt und - schlimmer - beim
  // Speichern nicht ein zweites Mal mit demselben Vorschlag verknuepft wird.
  const clearSuggestionParam = () => {
    if (searchParams.has('fromSuggestion')) {
      const next = new URLSearchParams(searchParams);
      next.delete('fromSuggestion');
      setSearchParams(next, { replace: true });
    }
  };
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [filterType, setFilterType] = useState<ProductType | 'all'>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ManualProduct | null>(null);

  // Vorschlag laden, aus dem heraus die Produktanlage gestartet wurde (Deep-Link-fähig)
  const { data: sourceSuggestion, isLoading: loadingSuggestion } = useQuery({
    queryKey: ['product-suggestion-prefill', fromSuggestionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_suggestions')
        .select('id, brand, product_name, product_type, barcode, notes')
        .eq('id', fromSuggestionId as string)
        .single();
      if (error) throw error;
      return data as {
        id: string;
        brand: string;
        product_name: string;
        product_type: ProductType;
        barcode: string | null;
        notes: string | null;
      };
    },
    enabled: !!fromSuggestionId,
  });

  useEffect(() => {
    if (sourceSuggestion) {
      setIsCreating(true);
    }
  }, [sourceSuggestion]);

  // Fetch manual products
  const { data: products, isLoading } = useQuery({
    queryKey: ['manual-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_manual', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ManualProduct[];
    },
  });

  // Vorschlag mit dem daraus erstellten Produkt verknüpfen und als erledigt markieren.
  // suggestionId wird explizit uebergeben (nicht aus dem Closure gelesen), damit
  // das nachgelagerte Entfernen des URL-Params keine Race-Condition ausloest.
  const linkSuggestion = useMutation({
    mutationFn: async ({ productId, suggestionId }: { productId: string; suggestionId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await (supabase
        .from('product_suggestions') as any)
        .update({
          created_product_id: productId,
          status: 'closed',
          processed_by: user?.id,
          processed_at: new Date().toISOString(),
        })
        .eq('id', suggestionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-suggestions'] });
    },
  });

  // Create product
  const createProduct = useMutation({
    mutationFn: async (data: ProductFormData) => {
      const { data: { user } } = await supabase.auth.getUser();

      // Parse ingredients into tags
      const ingredientsTags = data.ingredients_text
        .toLowerCase()
        .split(/[,;]/)
        .map((i) => i.trim())
        .filter((i) => i.length > 2);

      const { data: inserted, error } = await (supabase.from('products') as any)
        .insert({
          barcode: data.barcode,
          name: data.name,
          brand: data.brand,
          categories: data.categories || null,
          ingredients_text: data.ingredients_text,
          ingredients_tags: ingredientsTags,
          image_url: data.image_url || null,
          nutriscore_grade: data.nutriscore_grade || null,
          nutrition_facts: data.nutrition_facts,
          product_type: data.product_type,
          is_manual: true,
          created_by: user?.id,
          source: 'manual',
        })
        .select('id')
        .single();

      if (error) throw error;
      return inserted as { id: string };
    },
    onSuccess: (inserted) => {
      queryClient.invalidateQueries({ queryKey: ['manual-products'] });
      setIsCreating(false);
      toast.success('Produkt erstellt');
      if (fromSuggestionId) {
        linkSuggestion.mutate({ productId: inserted.id, suggestionId: fromSuggestionId });
        clearSuggestionParam();
      }
    },
    onError: (error: Error) => {
      if (error.message.includes('duplicate key')) {
        toast.error('Ein Produkt mit diesem Barcode existiert bereits');
      } else {
        toast.error(`Fehler: ${error.message}`);
      }
    },
  });

  // Update product
  const updateProduct = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProductFormData }) => {
      const ingredientsTags = data.ingredients_text
        .toLowerCase()
        .split(/[,;]/)
        .map((i) => i.trim())
        .filter((i) => i.length > 2);

      const { error } = await (supabase
        .from('products') as any)
        .update({
          barcode: data.barcode,
          name: data.name,
          brand: data.brand,
          categories: data.categories || null,
          ingredients_text: data.ingredients_text,
          ingredients_tags: ingredientsTags,
          image_url: data.image_url || null,
          nutriscore_grade: data.nutriscore_grade || null,
          nutrition_facts: data.nutrition_facts,
          product_type: data.product_type,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manual-products'] });
      setEditingProduct(null);
      toast.success('Produkt aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Delete product
  const deleteProduct = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manual-products'] });
      toast.success('Produkt gelöscht');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Filter products
  const filteredProducts = products?.filter((p) => {
    if (filterType !== 'all' && p.product_type !== filterType) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.name.toLowerCase().includes(query) ||
      p.brand.toLowerCase().includes(query) ||
      p.barcode.includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
              <Package className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Manuelle Produkte</h1>
              <p className="text-slate-500">Produkte manuell hinzufügen und verwalten</p>
            </div>
          </div>
          {fromSuggestionId && loadingSuggestion ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Vorschlag wird geladen...
            </div>
          ) : (
            !isCreating && !editingProduct && (
              <button
                onClick={() => setIsCreating(true)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Neues Produkt
              </button>
            )
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{products?.length || 0}</div>
              <div className="text-sm text-slate-500">Manuelle Produkte</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Apple className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">
                {products?.filter((p) => p.product_type === 'food').length || 0}
              </div>
              <div className="text-sm text-slate-500">Lebensmittel</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">
                {products?.filter((p) => p.product_type === 'cosmetic').length || 0}
              </div>
              <div className="text-sm text-slate-500">Kosmetik</div>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingProduct) && (
        <ProductForm
          key={editingProduct?.id || sourceSuggestion?.id || 'new'}
          product={editingProduct || undefined}
          prefill={
            !editingProduct && sourceSuggestion
              ? {
                  barcode: sourceSuggestion.barcode || '',
                  name: sourceSuggestion.product_name,
                  brand: sourceSuggestion.brand,
                  product_type: sourceSuggestion.product_type,
                  notes: sourceSuggestion.notes,
                }
              : undefined
          }
          onSave={(data) => {
            if (editingProduct) {
              updateProduct.mutate({ id: editingProduct.id, data });
            } else {
              createProduct.mutate(data);
            }
          }}
          onCancel={() => {
            setIsCreating(false);
            setEditingProduct(null);
            clearSuggestionParam();
          }}
          isLoading={createProduct.isPending || updateProduct.isPending}
        />
      )}

      {/* Products List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        {/* Filter & Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          {/* Type Filter */}
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
            {[
              { value: 'all', label: 'Alle' },
              { value: 'food', label: 'Lebensmittel', icon: Apple },
              { value: 'cosmetic', label: 'Kosmetik', icon: Sparkles },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setFilterType(option.value as ProductType | 'all')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filterType === option.value
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {option.icon && <option.icon className="w-4 h-4" />}
                {option.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Suche nach Name, Marke oder Barcode..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Produkt</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Typ</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Barcode</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Erstellt</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-500" />
                  </td>
                </tr>
              ) : filteredProducts?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-500">
                    {searchQuery || filterType !== 'all'
                      ? 'Keine Produkte gefunden'
                      : 'Noch keine manuellen Produkte vorhanden'}
                  </td>
                </tr>
              ) : (
                filteredProducts?.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-10 h-10 object-contain rounded bg-slate-50"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                            <Package className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-slate-900">{product.name}</span>
                          <p className="text-sm text-slate-500">{product.brand}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {product.product_type === 'cosmetic' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-pink-600 bg-pink-50 px-2 py-1 rounded-full">
                          <Sparkles className="w-3 h-3" />
                          Kosmetik
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          <Apple className="w-3 h-3" />
                          Lebensmittel
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">{product.barcode}</code>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-slate-500">
                        {new Date(product.created_at).toLocaleDateString('de-DE')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditingProduct(product)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Dieses Produkt wirklich löschen?')) {
                              deleteProduct.mutate(product.id);
                            }
                          }}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
